import { type FormEvent, type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegion } from '~/app/region';
import { Button, Label, Select, TextField } from '~/components/ui';

export const CompanyForm = ({
  onSubmit,
  submitLabel,
  submitting = false,
  submissionError,
}: {
  onSubmit: (value: {
    name: string;
    website?: string;
    industryCode: string;
    companySizeCode: string;
    jobTitle?: string;
    primaryMarketCode: string;
    logoFile?: File;
  }) => void;
  submitLabel: string;
  submitting?: boolean;
  submissionError?: string;
}): ReactElement => {
  const { t } = useTranslation('onboarding');
  const { t: tv } = useTranslation('validation');
  const { region, state: regionState } = useRegion();
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [industry, setIndustry] = useState('ecommerce');
  const [size, setSize] = useState('medium');
  const [jobTitle, setJobTitle] = useState('');
  const [logo, setLogo] = useState<File>();
  const [error, setError] = useState('');
  const primaryMarketCode =
    regionState === 'ready'
      ? region === 'RU'
        ? 'ru'
        : region === 'GLOBAL'
          ? 'global'
          : undefined
      : undefined;
  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!primaryMarketCode) return;
    if (!name.trim()) {
      setError(tv('required'));
      return;
    }
    onSubmit({
      name: name.trim(),
      website: website.trim() || undefined,
      industryCode: industry,
      companySizeCode: size,
      jobTitle: jobTitle.trim() || undefined,
      primaryMarketCode,
      logoFile: logo,
    });
  };
  const readLogo = (file?: File): void => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError(tv('logoSize'));
      return;
    }
    setLogo(file);
  };
  return (
    <form className="grid gap-5" onSubmit={submit}>
      <div className="grid gap-5 sm:grid-cols-2">
        <Label htmlFor="company-name">
          {t('name')}
          <TextField
            id="company-name"
            autoComplete="organization"
            onChange={e => setName(e.target.value)}
            placeholder="Aurora Commerce"
            value={name}
          />
        </Label>
        <Label htmlFor="website">
          {t('website')}
          <TextField
            autoComplete="url"
            id="website"
            onChange={e => setWebsite(e.target.value)}
            placeholder="https://"
            type="url"
            value={website}
          />
        </Label>
        <Label htmlFor="industry">
          {t('industry')}
          <Select
            id="industry"
            onChange={e => setIndustry(e.target.value)}
            value={industry}
          >
            {['ecommerce', 'agency', 'gaming', 'brand'].map(value => (
              <option key={value} value={value}>
                {t(`industries.${value}`)}
              </option>
            ))}
          </Select>
        </Label>
        <Label htmlFor="size">
          {t('size')}
          <Select
            id="size"
            onChange={e => setSize(e.target.value)}
            value={size}
          >
            {['small', 'medium', 'large', 'enterprise'].map(value => (
              <option key={value} value={value}>
                {t(`sizes.${value}`)}
              </option>
            ))}
          </Select>
        </Label>
        <Label htmlFor="job">
          {t('jobTitle')}
          <TextField
            autoComplete="organization-title"
            id="job"
            onChange={e => setJobTitle(e.target.value)}
            value={jobTitle}
          />
        </Label>
        <Label htmlFor="market">
          {t('market')}
          <TextField
            className="cursor-not-allowed bg-hover"
            id="market"
            readOnly
            value={primaryMarketCode ? t(`markets.${primaryMarketCode}`) : ''}
          />
        </Label>
      </div>
      <div className="grid gap-2">
        <span className="text-xs font-semibold text-muted" id="logo-label">
          {t('logo')}
        </span>
        <input
          accept="image/*"
          aria-labelledby="logo-label"
          className="peer sr-only"
          id="logo"
          onChange={e => readLogo(e.target.files?.[0])}
          type="file"
        />
        <label
          className="flex min-h-11 cursor-pointer items-center gap-3 rounded-xl border border-border bg-input px-3.5 text-sm text-foreground transition hover:border-primary/40 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary"
          htmlFor="logo"
        >
          <span className="shrink-0 font-semibold text-primary-text">
            {t('chooseLogo')}
          </span>
          <span className="min-w-0 truncate text-muted">
            {logo?.name || t('noLogoSelected')}
          </span>
        </label>
      </div>
      {(error || submissionError) && (
        <p className="text-sm text-rose-300" role="alert">
          {error || submissionError}
        </p>
      )}
      <Button
        className="mt-2 w-full sm:w-auto sm:justify-self-end"
        disabled={!primaryMarketCode || submitting}
        type="submit"
      >
        {submitting ? `${submitLabel}…` : submitLabel}
      </Button>
    </form>
  );
};
